import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import { DisputeRecord } from '../../../matching/routes/Disputes/core/response.types'
import { CLIENTS_PAGE_BREADCRUMBS } from '../../ClientsPage.consts'

import { ClientDisputesTable } from './components/ClientDisputesTable'
import { getClientDisputes } from './core/api'
import { PAGE_SIZE } from './core/consts'

/**
 * Вкладка «Диспуты» на карточке клиента (CPAYMENT-1732) — только история.
 *
 * Создание диспута живёт в очереди мэтчинга, закрытие — в журнале /matching/disputes; здесь нет
 * ни кнопок, ни модалок. Фильтров тоже нет: выборка уже ограничена одним клиентом.
 */
export const ClientDisputes: React.FC = () => {
  const { token } = useParams()
  const [items, setItems] = useState<DisputeRecord[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // Идентичность массива обязана быть стабильной: PageTitle завязан на неё в useEffect, а свежий
  // массив на каждом рендере зацикливает перерисовку страницы.
  const breadCrumbs = useMemo(
    () =>
      CLIENTS_PAGE_BREADCRUMBS.map((crumb) => ({
        ...crumb,
        path: crumb.path.replace('__token__', `${token}`),
        isActive: crumb.title === 'Диспуты',
      })),
    [token]
  )

  const load = useCallback(
    async (pg: number) => {
      if (!token) {
        return
      }

      setLoading(true)
      setError(false)
      try {
        const { data } = await getClientDisputes(token, { page: pg + 1, perPage: PAGE_SIZE })
        if (data.success) {
          setItems(data.data ?? [])
          setTotalRows(data.totalRows ?? 0)
        } else {
          setItems([])
          setTotalRows(0)
          setError(true)
        }
      } catch {
        setItems([])
        setTotalRows(0)
        setError(true)
      } finally {
        setLoading(false)
      }
    },
    [token]
  )

  useEffect(() => {
    setPage(0)
    load(0)
  }, [load])

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(selected)
  }

  const pageCount = Math.ceil(totalRows / PAGE_SIZE)

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs} />
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Диспуты</span>
          </h3>
        </div>
      </div>

      <div className='card'>
        <div className='card-body'>
          <ClientDisputesTable items={items} loading={loading} error={error} />
          {pageCount > 1 && (
            <TablePagination
              currentPage={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ClientDisputes
